import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { avatarTone, initials } from '../../chat.format';
import { ChatPresenceStatus } from '../../chat.model';

/**
 * Round avatar with initials as fallback and an optional presence dot.
 *
 * `src` is used as a plain <img> (the profile photos of `/chat/users` are
 * public URLs). With `auth` the image is fetched with the Authorization header
 * through [appAuthImage], for hosts whose avatars are protected file names.
 * The dot only exists for `online` / `away`: "offline" is also "unknown" for a
 * client, who never receives the entity's presence list (contract 3).
 *
 * Without a photo the initials sit on one of six soft tones of the theme
 * (`bg-soft-*` look: rgba of the theme colour at 18%), chosen from the name
 * so the same person always gets the same colour.
 */
@Component({
  selector: 'chat-avatar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<span class="chat-avatar" [ngClass]="'chat-avatar--tone-' + tone" [style.width.px]="size" [style.height.px]="size"
  [style.font-size.px]="fontSize">
  <img *ngIf="src && !auth && !failed" class="chat-avatar__img" [src]="src" alt="" (error)="failed = true">
  <img *ngIf="src && auth && !failed" class="chat-avatar__img" [appAuthImage]="src" alt="" (error)="failed = true">
  <span *ngIf="!src || failed" class="chat-avatar__initials" aria-hidden="true">{{ letters }}</span>
  <span *ngIf="status === 'online' || status === 'away'" class="chat-avatar__dot" [class.chat-avatar__dot--away]="status === 'away'"
    [style.width.px]="dotSize" [style.height.px]="dotSize"></span>
</span>`,
  styles: [`
:host { display: inline-block; flex-shrink: 0; line-height: 0; }
.chat-avatar { position: relative; display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; font-weight: 600; overflow: visible; box-sizing: border-box; }
.chat-avatar--tone-0 { background: rgba(var(--chat-accent-rgb, 64, 81, 137), .18); color: var(--chat-accent, #405189); }
.chat-avatar--tone-1 { background: rgba(var(--chat-success-rgb, 10, 179, 156), .18); color: var(--chat-success-ink, #08806f); }
.chat-avatar--tone-2 { background: rgba(var(--chat-info-rgb, 41, 156, 219), .18); color: var(--chat-info, #299cdb); }
.chat-avatar--tone-3 { background: rgba(var(--chat-warning-rgb, 247, 184, 75), .22); color: var(--chat-warning-ink, #a56a08); }
.chat-avatar--tone-4 { background: rgba(var(--chat-danger-rgb, 240, 101, 72), .18); color: var(--chat-danger, #f06548); }
.chat-avatar--tone-5 { background: rgba(var(--chat-secondary-rgb, 53, 119, 241), .18); color: var(--chat-secondary, #3577f1); }
.chat-avatar__img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; display: block; }
.chat-avatar__initials { line-height: 1; letter-spacing: .02em; }
.chat-avatar__dot { position: absolute; right: -1px; bottom: -1px; border-radius: 50%; background: var(--chat-success, #0ab39c); border: 2px solid var(--chat-surface, #ffffff); box-sizing: border-box; transition: background var(--chat-motion, .18s) ease; }
.chat-avatar__dot--away { background: var(--chat-warning, #f7b84b); }
`]
})
export class ChatAvatarComponent implements OnChanges {
  @Input() src?: string | null;
  @Input() name?: string;
  @Input() size = 40;
  @Input() status: ChatPresenceStatus | null = null;
  @Input() auth = false;

  letters = '?';
  tone = 0;
  failed = false;

  get fontSize(): number {
    return Math.max(10, Math.floor(this.size / 3));
  }

  get dotSize(): number {
    return this.size >= 36 ? 12 : 9;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['name']) {
      this.letters = initials(this.name);
      this.tone = avatarTone(this.name);
    }
    if (changes['src']) {
      this.failed = false;
    }
  }
}
